import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { projectPermission } from '../../utils/permissions';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import {
  fetchTaskById, updateTask, deleteTask,
  addComment, updateComment, deleteComment,
  addReply
} from '../../store/slices/tasksSlice';
import { fetchProjectMembers, fetchProjectById } from '../../store/slices/projectsSlice';
import { fetchSprints } from '../../store/slices/sprintsSlice';
import DependencyManager from '../../components/common/DependencyManager';
import CloneTaskModal from '../../components/common/CloneTaskModal';
import ConfirmModal from '../../components/common/ConfirmModal';
import SubtaskSection from '../../components/common/SubtaskSection';
import RichTextEditor, { RichTextDisplay } from '../../components/common/RichTextEditor';
import FileAttachments from '../../components/common/FileAttachments';
import ParentSelector from '../../components/common/ParentSelector';
import TicketTypeSelector, { TicketTypeIcon, TICKET_TYPES } from '../../components/common/TicketTypeSelector';
import SearchableSelect from '../../components/common/SearchableSelect';
import api from '../../services/api';
import { Avatar } from '../../components/common/Avatar';
import { useTaskTypes } from '../../hooks/useTaskTypes';
import { validationMessages } from '../../utils/validationMessages';
import './TaskDetail.css';

const MAX_THREAD_DEPTH = 5;

// Priority configuration
const PRIORITIES = {
  highest: { color: '#ff5630', bgColor: '#ffebe6', label: 'Highest', icon: '⬆⬆' },
  high: { color: '#ff7452', bgColor: '#ffebe6', label: 'High', icon: '⬆' },
  medium: { color: '#ffab00', bgColor: '#fff7e6', label: 'Medium', icon: '━' },
  low: { color: '#2684ff', bgColor: '#deebff', label: 'Low', icon: '⬇' },
  lowest: { color: '#57d9a3', bgColor: '#e3fcef', label: 'Lowest', icon: '⬇⬇' },
};

// Status configuration
const STATUSES = {
  backlog: { color: '#6b778c', bgColor: '#f4f5f7', label: 'Backlog' },
  in_progress: { color: '#0052cc', bgColor: '#deebff', label: 'In Progress' },
  review: { color: '#ff8b00', bgColor: '#fff7e6', label: 'Review' },
  done: { color: '#006644', bgColor: '#e3fcef', label: 'Done' },
};

// Recursive CommentThread component for threaded comments
const CommentThread = ({
  comment, depth, user, projectId, taskId,
  editingCommentId, editingCommentContent, setEditingCommentContent,
  savingComment, handleEditComment, handleSaveComment, handleCancelEditComment,
  handleDeleteComment, canEditComment,
  replyingToId, replyContent, setReplyContent, submittingReply,
  handleStartReply, handleCancelReply, handleSubmitReply,
  collapsedThreads, toggleThreadCollapse,
  getFileIcon, formatFileSize, handleDownloadCommentAttachment,
}) => {
  const c = comment;
  const hasReplies = c.replies && c.replies.length > 0;
  const isCollapsed = collapsedThreads[c.id];
  const canReply = depth < MAX_THREAD_DEPTH && !c.is_deleted;

  return (
    <div className={`comment-thread depth-${Math.min(depth, 5)}`}>
      <div className={`comment-item ${editingCommentId === c.id ? 'comment-editing' : ''} ${c.is_deleted ? 'comment-deleted' : ''}`}>
        {depth > 0 && <div className="thread-connector" />}

        <div className="comment-header">
          <Avatar user={c.author} size="sm" className="comment-avatar" />
          <div className="comment-meta">
            <span className="comment-author">
              {c.is_deleted ? 'Deleted' : (c.author?.full_name || 'Unknown User')}
            </span>
            <span className="comment-time">
              {format(new Date(c.created_at), 'MMM d, yyyy \'at\' h:mm a')}
            </span>
            {c.is_edited && !c.is_deleted && (
              <span className="comment-edited" title={c.edited_at ? `Last edited: ${format(new Date(c.edited_at), 'MMM d, yyyy h:mm a')}` : ''}>
                (edited)
              </span>
            )}
          </div>
          {hasReplies && (
            <button
              className="thread-toggle"
              onClick={() => toggleThreadCollapse(c.id)}
              title={isCollapsed ? 'Expand replies' : 'Collapse replies'}
              aria-expanded={!isCollapsed}
            >
              {isCollapsed ? `▸ ${c.replies.length} ${c.replies.length === 1 ? 'reply' : 'replies'}` : '▾'}
            </button>
          )}
        </div>

        {editingCommentId === c.id ? (
          <div className="comment-edit-form">
            <RichTextEditor
              value={editingCommentContent}
              onChange={setEditingCommentContent}
              placeholder="Edit your comment..."
              minHeight={80}
              maxHeight={250}
              enableMentions={true}
              projectId={projectId}
              enableImageUpload={true}
              imageUploadEntityType="task_description"
              imageUploadEntityId={taskId}
            />
            <div className="comment-edit-actions">
              <button className="btn btn-sm btn-primary" onClick={handleSaveComment} disabled={!editingCommentContent.trim() || savingComment}>
                {savingComment ? 'Saving...' : 'Save'}
              </button>
              <button className="btn btn-sm btn-ghost" onClick={handleCancelEditComment} disabled={savingComment}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="comment-body">
              {c.is_deleted ? (
                <em className="deleted-message">[This comment has been deleted]</em>
              ) : (
                <RichTextDisplay content={c.content} />
              )}
            </div>

            {!c.is_deleted && c.attachments && c.attachments.length > 0 && (
              <div className="comment-attachments">
                {c.attachments.map((att) => (
                  <div key={att.id} className="comment-attachment-item">
                    <span className="comment-attachment-icon">{getFileIcon(att.content_type)}</span>
                    <span className="comment-attachment-name" onClick={() => handleDownloadCommentAttachment(att)} title={`Download ${att.original_filename}`}>
                      {att.original_filename}
                    </span>
                    <span className="comment-attachment-size">{att.file_size_formatted || formatFileSize(att.file_size)}</span>
                  </div>
                ))}
              </div>
            )}

            {!c.is_deleted && (
              <div className="comment-actions">
                {canReply && (
                  <button className="comment-action-btn" onClick={() => handleStartReply(c.id)} title="Reply to this comment">
                    Reply
                  </button>
                )}
                {canEditComment(c) && (
                  <button className="comment-action-btn" onClick={() => handleEditComment(c)} title="Edit comment">
                    Edit
                  </button>
                )}
                {(c.author?.id === user?.id || ['super_admin', 'admin'].includes(user?.role)) && (
                  <button className="comment-action-btn comment-action-delete" onClick={() => handleDeleteComment(c.id)} title="Delete comment">
                    Delete
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {replyingToId === c.id && (
          <div className="reply-form">
            <RichTextEditor
              value={replyContent}
              onChange={setReplyContent}
              placeholder="Write a reply..."
              minHeight={60}
              maxHeight={200}
              enableMentions={true}
              projectId={projectId}
              enableImageUpload={true}
              imageUploadEntityType="task_description"
              imageUploadEntityId={taskId}
            />
            <div className="reply-form-actions">
              <button className="btn btn-sm btn-primary" onClick={() => handleSubmitReply(c.id)} disabled={!replyContent.trim() || submittingReply}>
                {submittingReply ? 'Posting...' : 'Reply'}
              </button>
              <button className="btn btn-sm btn-ghost" onClick={handleCancelReply} disabled={submittingReply}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {hasReplies && !isCollapsed && (
        <div className="comment-replies">
          {c.replies.map((reply) => (
            <CommentThread
              key={reply.id}
              comment={reply}
              depth={depth + 1}
              user={user}
              projectId={projectId}
              taskId={taskId}
              editingCommentId={editingCommentId}
              editingCommentContent={editingCommentContent}
              setEditingCommentContent={setEditingCommentContent}
              savingComment={savingComment}
              handleEditComment={handleEditComment}
              handleSaveComment={handleSaveComment}
              handleCancelEditComment={handleCancelEditComment}
              handleDeleteComment={handleDeleteComment}
              canEditComment={canEditComment}
              replyingToId={replyingToId}
              replyContent={replyContent}
              setReplyContent={setReplyContent}
              submittingReply={submittingReply}
              handleStartReply={handleStartReply}
              handleCancelReply={handleCancelReply}
              handleSubmitReply={handleSubmitReply}
              collapsedThreads={collapsedThreads}
              toggleThreadCollapse={toggleThreadCollapse}
              getFileIcon={getFileIcon}
              formatFileSize={formatFileSize}
              handleDownloadCommentAttachment={handleDownloadCommentAttachment}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * TaskDetailCore - Mode-agnostic business logic component
 * Renders task details with full editing capabilities
 *
 * @param {string} identifier - The task ID or key to display (supports both formats for backward compat)
 * @param {string} identifierType - 'id' | 'key' - how to interpret the identifier
 * @param {string} taskId - Legacy prop: The task ID to display (deprecated, use identifier)
 * @param {string} renderMode - Rendering mode: 'fullpage' | 'modal' | 'sidepanel'
 * @param {function} onClose - Callback when modal/sidepanel should close
 * @param {function} onDeleted - Callback when task is deleted
 */
const TaskDetailCore = ({ identifier, identifierType = 'id', taskId, renderMode = 'fullpage', onClose, onDeleted }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { currentTask, loading } = useSelector((state) => state.tasks);
  const { members, currentProject } = useSelector((state) => state.projects);
  const { sprints } = useSelector((state) => state.sprints);

  // Derive time tracking method from project settings.
  // Default to 'time_tracking' when the field is absent (treat missing as time_tracking).
  const timeTrackingMethod = currentProject?.time_tracking_method || 'time_tracking';
  const showStoryPoints = timeTrackingMethod === 'story_points';
  const showTimeTracking = timeTrackingMethod === 'time_tracking';

  // Load task types from API (projectId comes from currentTask after load)
  const { taskTypes, subtaskTypes, mainTypes, getTypeByKey } = useTaskTypes(currentTask?.project_id);

  // Support both old (taskId prop) and new (identifier/identifierType) props
  // For now, we only support ID-based lookup internally (upgrade later when backend supports key lookup)
  const effectiveTaskId = identifier || taskId;

  // UI State
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('comments');
  const [expandedDescDiffs, setExpandedDescDiffs] = useState({});
  const [hasDraft, setHasDraft] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const titleInputRef = useRef(null);
  const descriptionBlurTimeoutRef = useRef(null);
  const descriptionEditorRef = useRef(null);

  // Data State
  const [attachments, setAttachments] = useState([]);
  const [children, setChildren] = useState([]);
  const [workflowStatuses, setWorkflowStatuses] = useState([]);
  const [missingFields, setMissingFields] = useState([]);
  const [threadedComments, setThreadedComments] = useState([]);

  // Comment State
  const [comment, setComment] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [hasCommentDraft, setHasCommentDraft] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [collapsedThreads, setCollapsedThreads] = useState({});
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: null,
    isLoading: false
  });

  // Permissions
  const isViewer = user?.role === 'viewer';

  // viewer: read-only — no mutations of any kind
  const canEditTask = !isViewer;

  const canDeleteTask = projectPermission(user, members, {
    projectRoles: ['admin', 'manager'],
    systemFallbackRoles: ['admin'],
  });

  const canCloneTask = projectPermission(user, members, {
    projectRoles: ['admin', 'manager', 'member'],
    systemFallbackRoles: ['admin', 'manager'],
  });

  const canManageDependencies = !isViewer && (
    projectPermission(user, members, {
      projectRoles: ['admin', 'manager', 'member'],
      systemFallbackRoles: ['admin', 'manager'],
    }) ||
    currentTask?.assignee?.id === user?.id
  );

  const availableSprints = sprints.filter(s => s.status === 'planned' || s.status === 'active');

  // Check if current task is locked in a completed sprint
  const isInCompletedSprint = currentTask?.sprint_id && 
    sprints.some(s => s.id === currentTask.sprint_id && s.status === 'completed');

  // Draft keys for localStorage
  const draftKey = `task-draft-${effectiveTaskId}-${user?.id}`;
  const commentDraftKey = `task-comment-draft-${effectiveTaskId}-${user?.id}`;

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (!currentTask) return false;
    return (
      editData.title !== currentTask.title ||
      editData.description !== (currentTask.description || '') ||
      editData.task_type !== currentTask.task_type ||
      editData.status !== currentTask.status ||
      editData.priority !== currentTask.priority ||
      String(editData.assignee_id || '') !== String(currentTask.assignee?.id || '') ||
      String(editData.sprint_id || '') !== String(currentTask.sprint_id || '') ||
      String(editData.estimated_hours || '') !== String(currentTask.estimated_hours || '') ||
      String(editData.story_points || '') !== String(currentTask.story_points || '') ||
      String(editData.actual_hours || '') !== String(currentTask.actual_hours || '') ||
      editData.parent_id !== (currentTask.parent_id || null)
    );
  }, [editData, currentTask]);

  // Save draft to localStorage
  const saveDraft = useCallback(() => {
    if (hasChanges) {
      localStorage.setItem(draftKey, JSON.stringify(editData));
      setHasDraft(true);
    }
  }, [draftKey, editData, hasChanges]);

  // Load draft from localStorage
  const loadDraft = useCallback(() => {
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setEditData(draft);
        setHasDraft(true);
        return true;
      } catch (e) {
        localStorage.removeItem(draftKey);
      }
    }
    return false;
  }, [draftKey]);

  // Discard draft
  const discardDraft = useCallback(() => {
    // Cancel any pending description-blur auto-save so it can't re-save after we clear
    if (descriptionBlurTimeoutRef.current) {
      clearTimeout(descriptionBlurTimeoutRef.current);
      descriptionBlurTimeoutRef.current = null;
    }
    localStorage.removeItem(draftKey);
    setHasDraft(false);
    if (currentTask) {
      setEditData({
        title: currentTask.title,
        description: currentTask.description || '',
        task_type: currentTask.task_type,
        status: currentTask.status,
        priority: currentTask.priority,
        assignee_id: currentTask.assignee?.id || '',
        sprint_id: currentTask.sprint_id || '',
        estimated_hours: currentTask.estimated_hours || '',
        story_points: currentTask.story_points || '',
        actual_hours: currentTask.actual_hours || '',
        parent_id: currentTask.parent_id || null,
      });
    }
    setIsEditing(false);
    setIsEditingTitle(false);
    setIsEditingDescription(false);
    setMissingFields([]);
  }, [draftKey, currentTask]);

  // Check if there's an unsaved comment (text or attachments)
  const hasUnsavedComment = useMemo(() => {
    return comment.trim().length > 0 || commentAttachments.length > 0;
  }, [comment, commentAttachments]);

  // Auto-save comment draft to localStorage
  useEffect(() => {
    if (comment.trim()) {
      localStorage.setItem(commentDraftKey, comment);
    } else {
      localStorage.removeItem(commentDraftKey);
    }
  }, [comment, commentDraftKey]);

  // Load comment draft from localStorage on mount
  useEffect(() => {
    const savedComment = localStorage.getItem(commentDraftKey);
    if (savedComment) {
      setComment(savedComment);
      setHasCommentDraft(true);
    }
  }, [commentDraftKey]);

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges || hasDraft || hasUnsavedComment) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges, hasDraft, hasUnsavedComment]);

  // Data fetching
  const fetchAttachments = useCallback(async () => {
    try {
      const response = await api.get(`/attachments/entity/task/${effectiveTaskId}`);
      setAttachments(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
    }
  }, [effectiveTaskId]);

  const fetchChildren = useCallback(async () => {
    try {
      const response = await api.get(`/hierarchy/tasks/${effectiveTaskId}/children`);
      setChildren(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch children:', error);
    }
  }, [effectiveTaskId]);

  const fetchWorkflowStatuses = useCallback(async (projectId) => {
    try {
      const response = await api.get(`/workflows/projects/${projectId}/statuses`);
      setWorkflowStatuses(response.data.items || []);
    } catch (error) {
      setWorkflowStatuses([
        { key: 'backlog', name: 'Backlog' },
        { key: 'in_progress', name: 'In Progress' },
        { key: 'review', name: 'Review' },
        { key: 'done', name: 'Done' },
      ]);
    }
  }, []);

  const fetchThreadedCommentsData = useCallback(async () => {
    try {
      const response = await api.get(`/tasks/${effectiveTaskId}/comments/threaded`);
      setThreadedComments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch threaded comments:', error);
    }
  }, [effectiveTaskId]);

  useEffect(() => {
    dispatch(fetchTaskById(effectiveTaskId));
    fetchAttachments();
    fetchChildren();
    fetchThreadedCommentsData();
  }, [dispatch, effectiveTaskId, fetchAttachments, fetchChildren, fetchThreadedCommentsData]);

  useEffect(() => {
    if (currentTask?.project_id) {
      dispatch(fetchProjectMembers(currentTask.project_id));
      dispatch(fetchSprints({ projectId: currentTask.project_id }));
      fetchWorkflowStatuses(currentTask.project_id);
      if (!currentProject || currentProject.id !== currentTask.project_id) {
        dispatch(fetchProjectById(currentTask.project_id));
      }
    }
  }, [dispatch, currentTask?.project_id, fetchWorkflowStatuses]); // eslint-disable-line

  useEffect(() => {
    if (currentTask) {
      // Check for existing draft first
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          setEditData(draft);
          setHasDraft(true);
          return;
        } catch (e) {
          localStorage.removeItem(draftKey);
        }
      }
      // No draft, use current task data
      setEditData({
        title: currentTask.title,
        description: currentTask.description || '',
        task_type: currentTask.task_type,
        status: currentTask.status,
        priority: currentTask.priority,
        assignee_id: currentTask.assignee?.id || '',
        sprint_id: currentTask.sprint_id || '',
        estimated_hours: currentTask.estimated_hours || '',
        story_points: currentTask.story_points || '',
        actual_hours: currentTask.actual_hours || '',
        parent_id: currentTask.parent_id || null,
      });
    }
  }, [currentTask?.id, draftKey]); // eslint-disable-line

  // ── Mandatory-field completion helpers ──────────────────────────────────
  const COMPLETION_FIELD_LABELS = {
    assignee: 'Assignee',
    story_points: 'Story Points',
    actual_hours: 'Actual Hours',
    estimated_hours: 'Estimated Hours',
    parent: 'Parent Task',
  };

  // Handlers
  const handleSave = async () => {
    // Block save if a media upload is still in progress in the description editor.
    // Also keep the editor open (clear blur timeout) so the upload can finish and
    // the placeholder can be replaced before the user tries again.
    const uploadsActive = descriptionEditorRef.current?.hasActiveUploads?.();
    const descriptionHasPlaceholder = /Uploading (image|video) \[/.test(editData.description || '');
    if (uploadsActive || descriptionHasPlaceholder) {
      if (descriptionBlurTimeoutRef.current) {
        clearTimeout(descriptionBlurTimeoutRef.current);
        descriptionBlurTimeoutRef.current = null;
      }
      toast.warn('Please wait for the image/video to finish uploading before saving.');
      return;
    }
    // Cancel any pending description blur timeout to prevent it from
    // re-saving a draft after we clear it below
    if (descriptionBlurTimeoutRef.current) {
      clearTimeout(descriptionBlurTimeoutRef.current);
      descriptionBlurTimeoutRef.current = null;
    }
    setSaving(true);
    try {
      const parentId = editData.parent_id === null ? -1 : editData.parent_id;
      const sprintId = currentTask.task_type === 'epic'
        ? null
        : (editData.sprint_id ? parseInt(editData.sprint_id) : null);
      const result = await dispatch(updateTask({
        taskId: currentTask.id,
        taskData: {
          ...editData,
          assignee_id: editData.assignee_id ? parseInt(editData.assignee_id) : null,
          sprint_id: sprintId,
          estimated_hours: editData.estimated_hours ? parseFloat(editData.estimated_hours) : null,
          story_points: editData.story_points ? parseInt(editData.story_points) : null,
          actual_hours: editData.actual_hours ? parseFloat(editData.actual_hours) : null,
          parent_id: parentId,
        },
      })).unwrap();
      // Cancel any description blur timeout that fired during the API call.
      // Clicking Save blurs the description editor, which schedules a 300ms saveDraft timeout.
      // If the API responds in <300ms, that timeout fires after the save with stale
      // hasChanges=true and writes a stale draft — causing hasDraft to stay true.
      if (descriptionBlurTimeoutRef.current) {
        clearTimeout(descriptionBlurTimeoutRef.current);
        descriptionBlurTimeoutRef.current = null;
      }
      // Clear draft after successful save
      localStorage.removeItem(draftKey);
      setHasDraft(false);
      // Immediately sync editData to the saved values so hasChanges becomes false
      // This prevents the save bar from flickering back due to stale editData
      setEditData({
        title: result.title,
        description: result.description || '',
        task_type: result.task_type,
        status: result.status,
        priority: result.priority,
        assignee_id: result.assignee?.id || '',
        sprint_id: result.sprint_id || '',
        estimated_hours: result.estimated_hours || '',
        story_points: result.story_points || '',
        actual_hours: result.actual_hours || '',
        parent_id: result.parent_id || null,
      });
      const taskTypeLabel = currentTask?.task_type ? currentTask.task_type.charAt(0).toUpperCase() + currentTask.task_type.slice(1) : 'Task';
      toast.success(`${taskTypeLabel} updated`);
      setIsEditing(false);
      setIsEditingTitle(false);
      setIsEditingDescription(false);
      fetchChildren();
    } catch (error) {
      if (error && typeof error === 'object' && error.error === 'mandatory_fields_missing') {
        const names = (error.fields || [])
          .map(f => COMPLETION_FIELD_LABELS[f] || f)
          .join(', ');
        toast.error(`Cannot close ticket. Please fill required fields: ${names}`);
        setMissingFields(error.fields || []);
      } else {
        toast.error(typeof error === 'string' ? error : 'Failed to update task');
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle title edit blur - save to draft
  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    if (hasChanges) {
      saveDraft();
    }
  };

  // Handle description edit blur - delayed to allow paste/toolbar interactions
  // Without delay, right-click paste or toolbar clicks cause the editor to close
  const handleDescriptionBlur = () => {
    // Clear any existing timeout
    if (descriptionBlurTimeoutRef.current) {
      clearTimeout(descriptionBlurTimeoutRef.current);
    }
    // Delay closing so that if focus returns to the editor (e.g. after paste
    // from context menu, toolbar click, or mention dropdown), we cancel the close
    descriptionBlurTimeoutRef.current = setTimeout(() => {
      // Don't close the editor while media uploads are still in progress —
      // unmounting it would orphan the upload and lose the placeholder replacement.
      if (descriptionEditorRef.current?.hasActiveUploads?.()) {
        descriptionBlurTimeoutRef.current = null;
        return;
      }
      setIsEditingDescription(false);
      if (hasChanges) {
        saveDraft();
      }
    }, 300);
  };

  // Cancel pending blur when editor regains focus
  const handleDescriptionFocus = () => {
    if (descriptionBlurTimeoutRef.current) {
      clearTimeout(descriptionBlurTimeoutRef.current);
      descriptionBlurTimeoutRef.current = null;
    }
  };

  // Start editing title
  const startEditingTitle = () => {
    setIsEditingTitle(true);
    setIsEditing(true);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  };

  // Start editing description
  const startEditingDescription = () => {
    setIsEditingDescription(true);
    setIsEditing(true);
  };

  const handleTaskRefresh = () => {
    dispatch(fetchTaskById(effectiveTaskId));
  };

  const handleDelete = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Task',
      message: `Are you sure you want to delete "${currentTask?.task_key}: ${currentTask?.title}"? This action cannot be undone.`,
      confirmText: 'Delete Task',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(deleteTask(currentTask.id)).unwrap();
          toast.success('Task deleted');
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));

          // Handle delete based on render mode
          if (renderMode === 'fullpage') {
            navigate(-1);
          } else {
            // For modal/sidepanel, call callbacks
            onDeleted?.();
            onClose?.();
          }
        } catch (error) {
          const message = typeof error === 'string' ? error : (error?.message || 'Failed to delete task');
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          toast.error(message);
        }
      }
    });
  };

  const handleClone = async () => {
    try {
      const response = await api.post(`/tasks/${currentTask.id}/clone`);
      const clonedTask = response.data;
      toast.success(`Task cloned: ${clonedTask.task_key}`);
      
      // Navigate to the cloned task
      if (renderMode === 'fullpage') {
        navigate(`/projects/${currentTask.project_id}/board/${clonedTask.task_key}`);
      } else {
        navigate(`/projects/${currentTask.project_id}/board/${clonedTask.task_key}`, {
          state: {
            viewMode: renderMode,
            returnTo: location.pathname
          }
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to clone task');
    }
  };

  const handleUploadComplete = (uploadedFiles) => {
    setAttachments(prev => [...uploadedFiles, ...prev]);
  };

  const handleDeleteComplete = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim() && commentAttachments.length === 0) return;

    setSubmittingComment(true);
    try {
      const result = await dispatch(addComment({
        taskId: currentTask.id,
        content: comment || '<p>Attachment</p>'
      })).unwrap();

      const commentId = result.comment?.id;

      if (commentAttachments.length > 0 && commentId) {
        for (const file of commentAttachments) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(
              `/api/v1/attachments/upload?entity_type=comment&entity_id=${commentId}`,
              {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
              }
            );
            if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
          } catch (uploadError) {
            console.error('Failed to upload attachment:', uploadError);
            toast.error(`Failed to upload ${file.name}`);
          }
        }
      }

      dispatch(fetchTaskById(effectiveTaskId));
      fetchThreadedCommentsData();
      setComment('');
      setCommentAttachments([]);
      setHasCommentDraft(false);
      localStorage.removeItem(commentDraftKey);
      const taskKey = currentTask?.task_key || 'task';
      toast.success(`Comment added to ${taskKey}`);
    } catch (error) {
      toast.error(error || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleCommentAttachFiles = useCallback((files) => {
    const validFiles = [...files];
    if (validFiles.length > 0) {
      setCommentAttachments(prev => [...prev, ...validFiles]);
    }
  }, []);

  const removeCommentAttachment = useCallback((index) => {
    setCommentAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type === 'application/pdf') return '📄';
    if (type.includes('word')) return '📝';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    return '📎';
  };

  const handleDownloadCommentAttachment = async (attachment) => {
    try {
      // Fetch a presigned S3 URL and let the browser download directly from S3.
      // Avoids loading the entire file into backend memory (fixes large-file timeouts).
      const response = await api.get(`/attachments/${attachment.id}/url`, { params: { download: true } });
      const url = response.data.download_url;
      const link = document.createElement('a');
      link.href = url;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteComment = (commentId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Comment',
      message: 'Are you sure you want to delete this comment? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(deleteComment(commentId)).unwrap();
          fetchThreadedCommentsData();
          const taskKey = currentTask?.task_key || 'task';
          toast.success(`Comment deleted from ${taskKey}`);
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error || 'Failed to delete comment');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const handleEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentContent(comment.content);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  const handleSaveComment = async () => {
    if (!editingCommentContent.trim()) {
      toast.error(validationMessages.task.comment.required);
      return;
    }
    setSavingComment(true);
    try {
      await dispatch(updateComment({
        commentId: editingCommentId,
        content: editingCommentContent
      })).unwrap();
      setEditingCommentId(null);
      setEditingCommentContent('');
      fetchThreadedCommentsData();
      const taskKey = currentTask?.task_key || 'task';
      toast.success(`Comment on ${taskKey} updated`);
    } catch (error) {
      toast.error(error || 'Failed to update comment');
    } finally {
      setSavingComment(false);
    }
  };

  const canEditComment = (comment) => comment.author?.id === user?.id;

  const handleStartReply = (commentId) => {
    setReplyingToId(commentId);
    setReplyContent('');
  };

  const handleCancelReply = () => {
    setReplyingToId(null);
    setReplyContent('');
  };

  const handleSubmitReply = async (parentCommentId) => {
    if (!replyContent.trim()) {
      toast.error(validationMessages.task.comment.required);
      return;
    }
    setSubmittingReply(true);
    try {
      await dispatch(addReply({
        taskId: currentTask.id,
        parentCommentId: parentCommentId,
        content: replyContent
      })).unwrap();
      setReplyingToId(null);
      setReplyContent('');
      fetchThreadedCommentsData();
      const taskKey = currentTask?.task_key || 'task';
      toast.success(`Reply added to ${taskKey}`);
    } catch (error) {
      toast.error(error || 'Failed to add reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const toggleThreadCollapse = (commentId) => {
    setCollapsedThreads(prev => ({ ...prev, [commentId]: !prev[commentId] }));
  };

  const getStatusConfig = (status) => STATUSES[status] || STATUSES.backlog;
  const getPriorityConfig = (priority) => PRIORITIES[priority] || PRIORITIES.medium;
  const getTypeConfig = (type) => getTypeByKey(type) || TICKET_TYPES.task;

  if (loading && !currentTask) {
    return (
      <div className="loading-state">
        <span className="spinner"></span>
        <p>Loading task...</p>
      </div>
    );
  }

  if (!currentTask) {
    return <div className="error-state">Task not found</div>;
  }

  const statusConfig = getStatusConfig(currentTask.status);
  const priorityConfig = getPriorityConfig(currentTask.priority);
  const typeConfig = getTypeConfig(currentTask.task_type);
  const parentKey = currentTask.parent?.task_key;
  const taskUrl = parentKey
    ? `/projects/${currentTask.project_id}/board/${parentKey}/${currentTask.task_key}`
    : `/projects/${currentTask.project_id}/board/${currentTask.task_key}`;
  const parentUrl = parentKey
    ? `/projects/${currentTask.project_id}/board/${parentKey}`
    : null;

  // Show breadcrumb in fullpage mode (above header) and modal/sidepanel mode (in header)
  const showStandaloneBreadcrumb = renderMode === 'fullpage';
  const showInlineHeader = renderMode === 'modal' || renderMode === 'sidepanel';

  return (
    <div className="task-detail-page">
      {/* Breadcrumb - Only in full-page mode (standalone) */}
      {showStandaloneBreadcrumb && (
        <nav className="task-breadcrumb" aria-label="Breadcrumb">
          <Link to="/projects">Projects</Link>
          <span className="breadcrumb-sep">/</span>
          <Link to={`/projects/${currentTask.project_id}/board`}>Board</Link>
          {parentKey && (
            <>
              <span className="breadcrumb-sep">/</span>
              <Link to={parentUrl} className="breadcrumb-link">{parentKey}</Link>
            </>
          )}
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">{currentTask.task_key}</span>
        </nav>
      )}

      {/* Header Section */}
      <header className="task-header">
        <div className="header-main">
          {/* Row 1: Breadcrumb Navigation (Modal/SidePanel only) */}
          {showInlineHeader && (
            <nav className="header-breadcrumb-row" aria-label="Breadcrumb">
              <Link to="/projects" className="breadcrumb-link">Projects</Link>
              <span className="breadcrumb-sep">/</span>
              <Link to={`/projects/${currentTask.project_id}/board`} className="breadcrumb-link">Board</Link>
              {parentKey && (
                <>
                  <span className="breadcrumb-sep">/</span>
                  <Link to={parentUrl} className="breadcrumb-link">{parentKey}</Link>
                </>
              )}
              <span className="breadcrumb-sep">/</span>
              <Link
                to={taskUrl}
                className="breadcrumb-link breadcrumb-ticket-key"
                onClick={(e) => {
                  // Allow right-click, ctrl-click, shift-click, middle-click to open in new tab
                  if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
                    return; // Let browser handle it naturally (opens full page in new tab)
                  }
                  // Normal left click: close modal/panel and navigate to full page
                  e.preventDefault();
                  onClose?.(); // Close modal/panel first
                  navigate(taskUrl); // Navigate without state = fullpage
                }}
                onContextMenu={(e) => {
                  // Allow context menu (right-click menu) to work naturally
                  // Don't prevent default - let browser show "Open in new tab" etc.
                }}
              >
                {currentTask.task_key}
              </Link>
            </nav>
          )}

          {/* Row 2: Icon + Ticket Key + Title */}
          <div className="header-title-row">
            <div className="header-title-left">
              <TicketTypeIcon type={currentTask.task_type} size="lg" config={getTypeByKey(currentTask.task_type)} />
              <span className="task-key">{currentTask.task_key}</span>
            </div>

            {canEditTask && isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                className="task-title-edit"
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                onBlur={handleTitleBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleBlur();
                  if (e.key === 'Escape') {
                    setIsEditingTitle(false);
                    if (!hasChanges) setIsEditing(false);
                  }
                }}
                placeholder="Task title"
                autoFocus
              />
            ) : (
              <h1
                className={`task-title ${canEditTask ? 'editable-title' : ''} ${hasDraft && editData.title !== currentTask.title ? 'has-draft' : ''}`}
                onClick={canEditTask ? startEditingTitle : undefined}
                title={canEditTask ? 'Click to edit title' : undefined}
              >
                {hasDraft ? editData.title : currentTask.title}
                {hasDraft && editData.title !== currentTask.title && (
                  <span className="draft-indicator">Draft</span>
                )}
                {canEditTask && (
                  <span className="edit-hint">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </span>
                )}
              </h1>
            )}
          </div>

          {/* Row 3: Status, Priority, Assignee Badges */}

          {/* Status, Priority, Assignee Row */}
          <div className="header-meta-row">
            {/* Status Badge */}
            <span
              className="meta-badge status-badge"
              style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}
            >
              {statusConfig.label}
            </span>

            {/* Priority Badge */}
            <span
              className="meta-badge priority-badge"
              style={{ backgroundColor: priorityConfig.bgColor, color: priorityConfig.color }}
              title={`Priority: ${priorityConfig.label}`}
            >
              <span className="priority-icon">{priorityConfig.icon}</span>
              {priorityConfig.label}
            </span>

            {/* Assignee */}
            {currentTask.assignee ? (
              <div className="header-assignee">
                <Avatar user={currentTask.assignee} size="sm" className="assignee-avatar" />
                <span className="assignee-name">{currentTask.assignee.first_name} {currentTask.assignee.last_name}</span>
              </div>
            ) : (
              <span className="no-assignee">Unassigned</span>
            )}

          </div>
        </div>

        {/* Action Buttons */}
        <div className="header-actions">
          {!isEditing ? (
            <>
<button className="btn btn-secondary" disabled={!canCloneTask} onClick={() => setShowCloneModal(true)} title="Clone this task">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                </svg>
                Clone
              </button>
              <button className="btn btn-ghost btn-danger-hover" disabled={!canDeleteTask} onClick={handleDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Delete
              </button>
            </>
          ) : null}
        </div>
      </header>

      {/* Floating Save Bar - Shows when there are unsaved changes */}
      {canEditTask && (hasChanges || hasDraft) && (() => {
        const saveBarContent = (
          <div className="floating-save-bar">
            <div className="save-bar-content">
              <span className="save-bar-message">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                You have unsaved changes
              </span>
              <div className="save-bar-actions">
                <button className="btn btn-ghost" onClick={discardDraft} disabled={saving}>
                  Discard
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        );

        // In modal/sidepanel, portal the save bar into the container (outside scrollable content)
        if (renderMode === 'modal') {
          const container = document.querySelector('.task-modal-container');
          return container ? createPortal(saveBarContent, container) : saveBarContent;
        }
        if (renderMode === 'sidepanel') {
          const container = document.querySelector('.task-side-panel');
          return container ? createPortal(saveBarContent, container) : saveBarContent;
        }
        return saveBarContent;
      })()}

      {/* Main Content Layout */}
      <div className="task-content-layout">
        {/* Main Content Column */}
        <main className="task-main-content">
          {/* Description Card */}
          <section className="content-card description-card">
            <header className="card-header">
              <h2 className="card-title">
                Description
                {hasDraft && editData.description !== (currentTask.description || '') && (
                  <span className="draft-badge">Draft</span>
                )}
              </h2>
            </header>
            <div className="card-body">
              {canEditTask && isEditingDescription ? (
                <RichTextEditor
                  ref={descriptionEditorRef}
                  value={editData.description}
                  onChange={(value) => setEditData({ ...editData, description: value })}
                  placeholder="Add a description... Use @ to mention users"
                  minHeight={150}
                  maxHeight={400}
                  enableMentions={true}
                  projectId={currentTask?.project_id}
                  enableImageUpload={true}
                  imageUploadEntityType="task_description"
                  imageUploadEntityId={currentTask?.id}
                  onBlur={handleDescriptionBlur}
                  onFocus={handleDescriptionFocus}
                />
              ) : (hasDraft ? editData.description : currentTask.description) ? (
                <div
                  className={`description-content ${canEditTask ? 'editable-content' : ''} ${hasDraft && editData.description !== (currentTask.description || '') ? 'has-draft' : ''}`}
                  onClick={canEditTask ? startEditingDescription : undefined}
                  style={{ userSelect: 'text', cursor: canEditTask ? 'pointer' : 'text' }}
                >
                  <RichTextDisplay content={hasDraft ? editData.description : currentTask.description} />
                  {canEditTask && (
                    <span className="edit-overlay">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </span>
                  )}
                </div>
              ) : canEditTask ? (
                <div className="description-empty">
                  <button className="add-description-btn" onClick={startEditingDescription}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add a description...
                  </button>
                </div>
              ) : (
                <p className="description-empty-text">No description provided.</p>
              )}
            </div>
          </section>

          {/* Attachments - Compact inline section */}
          <section className="attachments-section">
            <div className="attachments-header">
              <span className="attachments-title">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                Attachments
                {attachments.length > 0 && <span className="attachments-count">{attachments.length}</span>}
              </span>
            </div>
            <FileAttachments
              entityType="task"
              entityId={currentTask?.id || effectiveTaskId}
              attachments={attachments}
              onUploadComplete={handleUploadComplete}
              onDeleteComplete={handleDeleteComplete}
              canUpload={canEditTask}
              canDelete={
                projectPermission(user, members, {
                  projectRoles: ['admin', 'manager'],
                  systemFallbackRoles: ['admin', 'manager'],
                }) ||
                (!isViewer && (
                  currentTask?.assignee?.id === user?.id ||
                  currentTask?.reporter?.id === user?.id
                ))
              }
              compact={true}
            />
          </section>

          {/* Subtasks Section - Jira-style inline creation */}
          <SubtaskSection
            parentTask={currentTask}
            children={children}
            onChildCreated={() => fetchChildren()}
            projectMembers={members}
            canEdit={canEditTask && (
              projectPermission(user, members, {
                projectRoles: ['admin', 'manager', 'member'],
                systemFallbackRoles: ['admin', 'manager'],
              }) ||
              currentTask?.assignee?.id === user?.id ||
              currentTask?.reporter?.id === user?.id
            )}
          />

          {/* Linked Work Items */}
          <section className="content-card">
            <DependencyManager
              taskId={currentTask.id}
              projectId={currentTask.project_id}
              canManage={canManageDependencies}
              onDependencyChange={handleTaskRefresh}
            />
          </section>

          {/* Activity Section with Tabs */}
          <section className="content-card activity-card">
            <header className="card-header">
              <div className="activity-tabs" role="tablist">
                <button
                  role="tab"
                  aria-selected={activeTab === 'comments'}
                  className={`activity-tab ${activeTab === 'comments' ? 'active' : ''}`}
                  onClick={() => setActiveTab('comments')}
                >
                  Comments
                  {threadedComments.length > 0 && <span className="tab-count">{threadedComments.length}</span>}
                </button>
                <button
                  role="tab"
                  aria-selected={activeTab === 'history'}
                  className={`activity-tab ${activeTab === 'history' ? 'active' : ''}`}
                  onClick={() => setActiveTab('history')}
                >
                  History
                </button>
                <button
                  role="tab"
                  aria-selected={activeTab === 'worklog'}
                  className={`activity-tab ${activeTab === 'worklog' ? 'active' : ''}`}
                  onClick={() => setActiveTab('worklog')}
                >
                  Work Log
                </button>
              </div>
            </header>

            <div className="card-body activity-body">
              {/* Comments Tab */}
              {activeTab === 'comments' && (
                <div className="comments-section" role="tabpanel">
                  {/* Comment Input - Always Visible */}
                  {hasCommentDraft && (
                    <div className="comment-draft-banner">
                      <span>You have an unsaved comment</span>
                      <button
                        className="discard-comment-draft-btn"
                        onClick={() => {
                          setComment('');
                          setCommentAttachments([]);
                          setHasCommentDraft(false);
                          localStorage.removeItem(commentDraftKey);
                        }}
                      >
                        Discard
                      </button>
                    </div>
                  )}
                  <form onSubmit={handleAddComment} className="comment-input-form">
                    <Avatar user={user} size="sm" className="comment-input-avatar" />
                    <div className="comment-input-wrapper">
                      <RichTextEditor
                        value={comment}
                        onChange={(val) => {
                          setComment(val);
                          if (hasCommentDraft && !val.trim()) setHasCommentDraft(false);
                        }}
                        placeholder="Add a comment... Use @ to mention users"
                        minHeight={80}
                        maxHeight={200}
                        enableAttachments={true}
                        onAttachFiles={handleCommentAttachFiles}
                        pendingAttachments={commentAttachments}
                        onRemoveAttachment={removeCommentAttachment}
                        enableMentions={true}
                        projectId={currentTask?.project_id}
                        enableImageUpload={true}
                        imageUploadEntityType="task_description"
                        imageUploadEntityId={currentTask?.id}
                      />
                      <div className="comment-input-actions">
                        <button
                          type="submit"
                          className="btn btn-primary btn-sm"
                          disabled={(!comment.trim() && commentAttachments.length === 0) || submittingComment}
                        >
                          {submittingComment ? 'Posting...' : 'Comment'}
                        </button>
                      </div>
                    </div>
                  </form>

                  {/* Comments List */}
                  <div className="comments-list">
                    {threadedComments.length > 0 ? (
                      threadedComments.map((c) => (
                        <CommentThread
                          key={c.id}
                          comment={c}
                          depth={0}
                          user={user}
                          projectId={currentTask?.project_id}
                          taskId={currentTask?.id}
                          editingCommentId={editingCommentId}
                          editingCommentContent={editingCommentContent}
                          setEditingCommentContent={setEditingCommentContent}
                          savingComment={savingComment}
                          handleEditComment={handleEditComment}
                          handleSaveComment={handleSaveComment}
                          handleCancelEditComment={handleCancelEditComment}
                          handleDeleteComment={handleDeleteComment}
                          canEditComment={canEditComment}
                          replyingToId={replyingToId}
                          replyContent={replyContent}
                          setReplyContent={setReplyContent}
                          submittingReply={submittingReply}
                          handleStartReply={handleStartReply}
                          handleCancelReply={handleCancelReply}
                          handleSubmitReply={handleSubmitReply}
                          collapsedThreads={collapsedThreads}
                          toggleThreadCollapse={toggleThreadCollapse}
                          getFileIcon={getFileIcon}
                          formatFileSize={formatFileSize}
                          handleDownloadCommentAttachment={handleDownloadCommentAttachment}
                        />
                      ))
                    ) : (
                      <div className="empty-comments">
                        <p>No comments yet. Be the first to comment!</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <div className="history-section" role="tabpanel">
                  {currentTask.activities?.length > 0 ? (
                    <div className="activity-timeline">
                      {currentTask.activities.map((activity) => {
                        const isDescChange = activity.activity_type === 'description_changed';
                        const isExpanded = expandedDescDiffs[activity.id];
                        const stripHtml = (html) => {
                          if (!html) return '';
                          return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                        };
                        const oldPlain = stripHtml(activity.old_value);
                        const newPlain = stripHtml(activity.new_value);
                        const isLong = oldPlain.length > 120 || newPlain.length > 120;
                        return (
                          <div key={activity.id} className="timeline-item">
                            <div className="timeline-dot" />
                            <div className="timeline-content">
                              <span className="timeline-user">
                                {activity.user?.first_name} {activity.user?.last_name}
                              </span>
                              <span className="timeline-action">
                                {activity.activity_type.replace(/_/g, ' ')}
                              </span>
                              {!isDescChange && activity.old_value && activity.new_value && (
                                <span className="timeline-change">
                                  <span className="old-value">{activity.old_value}</span>
                                  <span className="change-arrow">→</span>
                                  <span className="new-value">{activity.new_value}</span>
                                </span>
                              )}
                              <span className="timeline-time">
                                {format(new Date(activity.created_at), 'MMM d, yyyy \'at\' h:mm a')}
                              </span>
                              {isDescChange && (
                                <div className="desc-diff-wrapper">
                                  {!isLong ? (
                                    <div className="desc-diff-inline">
                                      <span className="desc-diff-old">{oldPlain || '(empty)'}</span>
                                      <span className="change-arrow">→</span>
                                      <span className="desc-diff-new">{newPlain || '(empty)'}</span>
                                    </div>
                                  ) : (
                                    <>
                                      <button
                                        className="desc-diff-toggle"
                                        onClick={() => setExpandedDescDiffs(prev => ({ ...prev, [activity.id]: !prev[activity.id] }))}
                                      >
                                        {isExpanded ? 'Hide changes' : 'Show changes'}
                                      </button>
                                      {isExpanded && (
                                        <div className="desc-diff-blocks">
                                          <div className="desc-diff-block desc-diff-block--before">
                                            <span className="desc-diff-label">Before</span>
                                            {activity.old_value
                                              ? <RichTextDisplay content={activity.old_value} />
                                              : <em className="desc-diff-empty">(empty)</em>}
                                          </div>
                                          <div className="desc-diff-block desc-diff-block--after">
                                            <span className="desc-diff-label">After</span>
                                            {activity.new_value
                                              ? <RichTextDisplay content={activity.new_value} />
                                              : <em className="desc-diff-empty">(empty)</em>}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="empty-history">
                      <p>No activity history available.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Work Log Tab */}
              {activeTab === 'worklog' && (
                <div className="worklog-section" role="tabpanel">
                  {showTimeTracking ? (
                    <div className="worklog-summary">
                      <div className="worklog-stat">
                        <span className="stat-label">Estimated</span>
                        <span className="stat-value">{currentTask.estimated_hours || 0}h</span>
                      </div>
                      <div className="worklog-stat">
                        <span className="stat-label">Logged</span>
                        <span className="stat-value">{currentTask.actual_hours || 0}h</span>
                      </div>
                      <div className="worklog-stat">
                        <span className="stat-label">Remaining</span>
                        <span className="stat-value">
                          {Math.max(0, (currentTask.estimated_hours || 0) - (currentTask.actual_hours || 0)).toFixed(1)}h
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="worklog-summary">
                      <div className="worklog-stat">
                        <span className="stat-label">Story Points</span>
                        <span className="stat-value">{currentTask.story_points || 0}</span>
                      </div>
                    </div>
                  )}
                  <div className="empty-worklog">
                    <p>Time logging is tracked automatically when task status changes.</p>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>

        {/* Sidebar */}
        <aside className="task-sidebar">
          {/* Details Card */}
          <div className="sidebar-card">
            <h3 className="sidebar-card-title">
              Details
              {hasDraft && <span className="draft-badge">Draft</span>}
            </h3>

            {/* Status */}
            <div className="detail-row">
              <span className="detail-label">Status</span>
              <div className="detail-select-wrapper">
                <select
                  className={`detail-select status-select status-${editData.status}`}
                  value={editData.status}
                  disabled={!canEditTask}
                  onChange={(e) => {
                    if (!canEditTask) return;
                    setEditData({ ...editData, status: e.target.value });
                    setIsEditing(true);
                    if (missingFields.length > 0) setMissingFields([]);
                  }}
                >
                  {(() => {
                    const baseKeys = workflowStatuses.length > 0
                      ? workflowStatuses.map(s => s.key).filter(k => k !== 'backlog')
                      : Object.keys(STATUSES).filter(k => k !== 'backlog');
                    const keys = (editData.status === 'backlog' && !baseKeys.includes('backlog'))
                      ? ['backlog', ...baseKeys]
                      : baseKeys;
                    return keys.map((statusKey) => (
                      <option key={statusKey} value={statusKey}>
                        {STATUSES[statusKey]?.label || statusKey.replace('_', ' ')}
                      </option>
                    ));
                  })()}
                </select>
              </div>
            </div>

            {/* Priority */}
            <div className="detail-row">
              <span className="detail-label">Priority</span>
              <div className="detail-select-wrapper">
                <select
                  className="detail-select priority-select"
                  value={editData.priority}
                  disabled={!canEditTask}
                  onChange={(e) => {
                    if (!canEditTask) return;
                    setEditData({ ...editData, priority: e.target.value });
                    setIsEditing(true);
                  }}
                >
                  {Object.entries(PRIORITIES).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Type */}
            <div className="detail-row">
              <span className="detail-label">Type</span>
              <TicketTypeSelector
                value={editData.task_type}
                onChange={(type) => {
                  if (!canEditTask) return;
                  setEditData({ ...editData, task_type: type });
                  setIsEditing(true);
                }}
                projectId={currentTask?.project_id}
                compact={false}
                allowedTypes={
                  taskTypes[currentTask?.task_type]?.isSubtaskType
                    ? Object.keys(subtaskTypes)
                    : Object.keys(mainTypes)
                }
              />
            </div>

            {/* Assignee */}
            <div className={`detail-row${missingFields.includes('assignee') ? ' field-error' : ''}`}>
              <span className="detail-label">
                Assignee
                {missingFields.includes('assignee') && (
                  <span className="field-error-icon" title="Required before closing ticket">!</span>
                )}
              </span>
              <div className="detail-select-wrapper">
                {canEditTask ? (
                  <SearchableSelect
                    options={members.map(m => ({
                      value: m.user.id,
                      label: `${m.user.first_name} ${m.user.last_name}`,
                      email: m.user.email
                    }))}
                    value={editData.assignee_id}
                    onChange={(assigneeId) => {
                      setEditData({ ...editData, assignee_id: assigneeId });
                      setIsEditing(true);
                      if (missingFields.includes('assignee') && assigneeId) {
                        setMissingFields(prev => prev.filter(f => f !== 'assignee'));
                      }
                    }}
                    placeholder="Unassigned"
                    searchPlaceholder="Search users..."
                    getOptionDescription={(opt) => opt.email}
                    size="sm"
                    clearable={true}
                    dropdownWidth={260}
                  />
                ) : (
                  <span className="detail-value-text">
                    {currentTask.assignee ? `${currentTask.assignee.first_name} ${currentTask.assignee.last_name}` : 'Unassigned'}
                  </span>
                )}
              </div>
            </div>

            {/* Reporter */}
            <div className="detail-row">
              <span className="detail-label">Reporter</span>
              {currentTask.reporter ? (
                <div className="user-value">
                  <Avatar user={currentTask.reporter} size="xs" className="user-avatar-sm" />
                  <span>{currentTask.reporter.first_name} {currentTask.reporter.last_name}</span>
                </div>
              ) : (
                <span className="empty-value">Unknown</span>
              )}
            </div>

            {/* Sprint */}
            <div className="detail-row">
              <span className="detail-label">Sprint</span>
              {currentTask.task_type === 'epic' ? (
                <span className="empty-value">Not applicable</span>
              ) : isInCompletedSprint ? (
                <span className="locked-value" title="Tickets in a closed sprint are locked for historical accuracy">
                  🔒 {sprints.find(s => s.id === currentTask.sprint_id)?.name || 'Completed Sprint'}
                </span>
              ) : canEditTask ? (
                <div className="detail-select-wrapper">
                  <select
                    className="detail-select sprint-select"
                    value={editData.sprint_id || ''}
                    onChange={(e) => {
                      setEditData({ ...editData, sprint_id: e.target.value || null });
                      setIsEditing(true);
                    }}
                  >
                    <option value="">No Sprint</option>
                    {availableSprints.map((sprint) => (
                      <option key={sprint.id} value={sprint.id}>
                        {sprint.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <span className="detail-value-text">
                  {sprints.find(s => s.id === currentTask.sprint_id)?.name || 'No Sprint'}
                </span>
              )}
            </div>

            {/* Parent */}
            {currentTask.task_type !== 'epic' && (
              <div className="detail-row">
                <span className="detail-label">Parent</span>
                {canEditTask ? (
                  <ParentSelector
                    projectId={currentTask.project_id}
                    taskId={currentTask.id}
                    taskType={editData.task_type}
                    currentParentId={editData.parent_id}
                    onChange={(parentId) => {
                      setEditData({ ...editData, parent_id: parentId });
                      setIsEditing(true);
                    }}
                  />
                ) : (
                  <span className="detail-value-text">
                    {currentTask.parent ? currentTask.parent.task_key : 'None'}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Estimation Card */}
          <div className="sidebar-card">
            <h3 className="sidebar-card-title">Time &amp; Estimates</h3>
            <div className="estimation-edit-form">
              {/* Story Points — shown only in story_points mode */}
              {showStoryPoints && (
                <div className={`estimation-row${missingFields.includes('story_points') ? ' field-error' : ''}`}>
                  <label>
                    Story Points
                    {missingFields.includes('story_points') && (
                      <span className="field-error-icon" title="Story Points required before closing">!</span>
                    )}
                  </label>
                  <input
                    type="number"
                    className="estimation-input"
                    value={editData.story_points}
                    disabled={!canEditTask}
                    onChange={(e) => {
                      if (!canEditTask) return;
                      setEditData({ ...editData, story_points: e.target.value });
                      setIsEditing(true);
                      if (missingFields.includes('story_points') && e.target.value > 0) {
                        setMissingFields(prev => prev.filter(f => f !== 'story_points'));
                      }
                    }}
                    placeholder="0"
                    min="0"
                    max="100"
                  />
                </div>
              )}

              {/* Estimated Hours — shown only in time_tracking mode */}
              {showTimeTracking && (
                <div className={`estimation-row${missingFields.includes('estimated_hours') ? ' field-error' : ''}`}>
                  <label>
                    Estimated Hours
                    {missingFields.includes('estimated_hours') && (
                      <span className="field-error-icon" title="Estimated Hours required before closing">!</span>
                    )}
                  </label>
                  <input
                    type="number"
                    className="estimation-input"
                    value={editData.estimated_hours}
                    disabled={!canEditTask}
                    onChange={(e) => {
                      if (!canEditTask) return;
                      setEditData({ ...editData, estimated_hours: e.target.value });
                      setIsEditing(true);
                      if (missingFields.includes('estimated_hours') && e.target.value > 0) {
                        setMissingFields(prev => prev.filter(f => f !== 'estimated_hours'));
                      }
                    }}
                    placeholder="0"
                    min="0"
                    step="0.5"
                  />
                </div>
              )}

              {/* Actual Hours — shown only in time_tracking mode */}
              {showTimeTracking && (
                <div className={`estimation-row${missingFields.includes('actual_hours') ? ' field-error' : ''}`}>
                  <label>
                    Actual Hours
                    {missingFields.includes('actual_hours') && (
                      <span className="field-error-icon" title="Required before closing ticket">!</span>
                    )}
                  </label>
                  <input
                    type="number"
                    className="estimation-input"
                    value={editData.actual_hours}
                    disabled={!canEditTask}
                    onChange={(e) => {
                      if (!canEditTask) return;
                      setEditData({ ...editData, actual_hours: e.target.value });
                      setIsEditing(true);
                      if (missingFields.includes('actual_hours') && e.target.value > 0) {
                        setMissingFields(prev => prev.filter(f => f !== 'actual_hours'));
                      }
                    }}
                    placeholder="0"
                    min="0"
                    step="0.25"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Dates Card */}
          <div className="sidebar-card dates-card">
            <div className="date-row">
              <span className="date-label">Created</span>
              <span className="date-value">{format(new Date(currentTask.created_at), 'MMM d, yyyy')}</span>
            </div>
            <div className="date-row">
              <span className="date-label">Updated</span>
              <span className="date-value">{format(new Date(currentTask.updated_at), 'MMM d, yyyy')}</span>
            </div>
          </div>
        </aside>
      </div>

      {showCloneModal && currentTask && (
        <CloneTaskModal
          task={currentTask}
          renderMode={renderMode}
          onClose={() => setShowCloneModal(false)}
          onSuccess={(clonedTask) => {
            if (renderMode === 'fullpage') {
              navigate(`/projects/${currentTask.project_id}/board/${clonedTask.task_key}`);
            } else {
              navigate(`/projects/${currentTask.project_id}/board/${clonedTask.task_key}`, {
                state: { viewMode: renderMode, returnTo: location.pathname }
              });
            }
          }}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        isLoading={confirmModal.isLoading}
      />
    </div>
  );
};

export default TaskDetailCore;
